import * as Sentry from "@sentry/react-native";
import { useQueries } from "@tanstack/react-query";
import dayjs from "dayjs";
import { router } from "expo-router";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Linking, ScrollView, StyleSheet } from "react-native";
import { CheckBox } from "~/components/CheckBox";
import { Divider } from "~/components/Divider";
import { IconButton } from "~/components/IconButton";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { useStore } from "~/mobx/utils/useStore";
import { axiosErrorSchema } from "~/schemas";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { shadow } from "~/utils/shadow";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const S = StyleSheet.create({
  bodyText: { fontSize: 14, lineHeight: 20 },
  errorText: { color: styleConstantsV2.colorError },
  tosCard: { borderRadius: styleConstantsV2.borderRadius4 },
  scrollViewContent: {
    backgroundColor: styleConstantsV2.colorWhite,
    flexGrow: 1,
  },
});

// TODO: Fetch this data from BE instead of hardcoding it
const generalTermsAndConditionsUrl =
  "https://evidensia.se/personuppgiftspolicy/allmanna-villkor/";
const personalDataPolicyUrl = "https://evidensia.se/personuppgiftspolicy/";

const OverviewScreen = observer(function OverviewScreen() {
  const {
    clinicId: standaloneClinicId,
    isNewUser,
    isStandAloneBookingFlow,
  } = useStandaloneBookingFlow();
  useProgressBarStep(isNewUser ? 8 : 7);

  const store = useStore();
  const errorAlert = useErrorAlertV2();
  const language = intl.locale;

  const [tcCheckbox, setTcCheckbox] = useState(false);

  const { clinicId, patientId, reasonId } = store.bookingStore;

  const isNewPatient = store.bookingStore.hasNewPetValues;

  assert(clinicId, "clinicId is required");
  assert(patientId, "patientId is required");
  assert(reasonId, "reasonId is required");

  const [clinicQuery, petQuery, reasonQuery] = useQueries({
    queries: [
      {
        queryKey: ["clinic", clinicId],
        queryFn: async () => {
          const clinic = await store.clinicsStore.getClinic(clinicId);
          return clinic;
        },
      },
      {
        queryKey: ["pet", patientId],
        queryFn: async () => {
          const pet = await store.petsStore.getPet(patientId);
          return pet;
        },
        enabled: isNewPatient === false,
      },
      {
        queryKey: ["reason", reasonId],
        queryFn: async () => {
          const reason =
            await store.appointmentReasonStore.getAppointmentReason(
              reasonId,
              clinicId,
            );
          return reason;
        },
      },
    ],
  });

  const isLoading =
    clinicQuery.isLoading || petQuery.isLoading || reasonQuery.isLoading;

  if (isLoading) {
    return (
      <View colorWhite flex>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  const isError =
    clinicQuery.isError || petQuery.isError || reasonQuery.isError;

  if (isError) {
    const error = new Error(
      `${clinicQuery.error ?? ""} ${petQuery.error ?? ""} ${reasonQuery.error ?? ""}`,
    );
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={error}
            title={intl.formatMessage({
              id: "FbCJFI",
              defaultMessage: "Something went wrong",
              description: "Error message title when fetching appointment data",
            })}
          />
          <ButtonV2
            onPress={() => {
              clinicQuery.refetch();
              reasonQuery.refetch();
              if (isNewPatient === false) {
                petQuery.refetch();
              }
            }}
            title={intl.formatMessage({
              id: "Bs6GJB",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching appointment data",
            })}
          />
        </View>
      </View>
    );
  }

  return (
    <>
      <View flex>
        <ScrollView contentContainerStyle={S.scrollViewContent}>
          <View paddingVertical32 flex colorWhite>
            <BrandText sizeH1 weightExtraBold alignCenter colorBlack>
              <FormattedMessage
                id="Exb2ky"
                defaultMessage="Confirm the booking"
                description="Title for the confirm booking screen"
              />
            </BrandText>
            <Spacer large />
            <View padding16>
              <BrandText sizeH3 weightBold colorBlack>
                <FormattedMessage
                  id="03+aWo"
                  defaultMessage="Summary"
                  description="Title for booking summary section"
                />
              </BrandText>
              <Spacer large />
              <BodyText sizeLarge weightRegular colorBlack>
                {_.capitalize(
                  dayjs(store.bookingStore.start)
                    .locale(language)
                    .format("dddd (YYYY-MM-DD) [-] HH:mm"),
                )}
              </BodyText>
              <Spacer small />
              <Divider />
              <Spacer small />
              <BodyText sizeLarge weightRegular colorBlack>
                {reasonQuery.data?.name}
              </BodyText>
              <Spacer small />
              <Divider />
              <Spacer small />
              <BodyText sizeLarge weightRegular colorBlack>
                {isNewPatient === false && !!petQuery.data?.name
                  ? petQuery.data.name
                  : store.bookingStore.newPetName}
              </BodyText>
              <Spacer small />
              <Divider />
              <Spacer small />
              <BodyText sizeLarge weightRegular colorBlack>
                {clinicQuery.data?.name}
              </BodyText>
              <Spacer large />

              <Spacer extraExtraLarge />
              <ContactDetails />
              <Spacer extraExtraLarge />
              <View
                paddingHorizontal16
                paddingVertical24
                colorPrimaryBg
                style={S.tosCard}
              >
                <BodyText colorBlack weightBold sizeSmall>
                  <FormattedMessage
                    id="od7cXt"
                    defaultMessage="You can cancel your visit no later than 24 hours before. Missed visits will be charged."
                    description="Description of the cancellation policy"
                  />
                </BodyText>
                <Spacer large />
                <View flexDirectionRow alignItemsCenter>
                  <CheckBox
                    checked={tcCheckbox}
                    onChange={(isChecked) => {
                      setTcCheckbox(isChecked);
                    }}
                    size={24}
                  />

                  <Spacer small />
                  <BodyText sizeSmall colorBlack>
                    <FormattedMessage
                      id="7u0nhU"
                      defaultMessage="I have read and agree to <tclink>the general terms and conditions</tclink> and read <pplink>the personal data policy</pplink>"
                      description="Personal data policy aggremment"
                      values={{
                        tclink: (chunk) => (
                          <BodyText
                            sizeSmall
                            colorBlack
                            weightBold
                            underline
                            onPress={() => {
                              Linking.openURL(generalTermsAndConditionsUrl);
                            }}
                          >
                            {chunk}
                          </BodyText>
                        ),
                        pplink: (chunk) => (
                          <BodyText
                            sizeSmall
                            colorBlack
                            weightBold
                            underline
                            onPress={() => {
                              Linking.openURL(personalDataPolicyUrl);
                            }}
                          >
                            {chunk}
                          </BodyText>
                        ),
                      }}
                    />
                  </BodyText>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
      <View
        paddingHorizontal24
        paddingVertical32
        colorWhite
        style={{
          ...shadow(7),
        }}
      >
        <ButtonV2
          medium
          title={intl.formatMessage({
            id: "uGMKxR",
            defaultMessage: "Confirm",
            description: "Confirm button",
          })}
          onPress={async () => {
            try {
              store.bookingStore.setIsTosAccepted(tcCheckbox);

              if (isNewUser) {
                // Check that all the required new user values are present
                if (store.bookingStore.hasNewUserValues === false) {
                  throw new Error("New user values are missing");
                }

                try {
                  console.log("😼 Creating new user");

                  // Create new user
                  await store.authStore.createNewUser({
                    firstName: store.bookingStore.newClientFirstName as string,
                    lastName: store.bookingStore.newClientLastName as string,
                    phoneNumber: store.bookingStore
                      .newClientPhoneNumber as string,
                    email: store.bookingStore.newClientEmail as string,
                    city: store.bookingStore.newClientCity as string,
                    streetAddress: store.bookingStore
                      .newClientStreetAddress as string,
                    zipCode: store.bookingStore.newClientZipCode as string,
                  });

                  //Log event to the booking data layer
                  bookingDataLayerPush({
                    event: "customer_create_web_app",
                  });
                } catch (error: any) {
                  console.log("😼 Error creating new user");

                  // The BE enforces a minimum age of 18 on registration and
                  // responds with 403 + `under_18_restriction`. Abort the
                  // booking cleanly with a dedicated message instead of
                  // showing the generic error.
                  if (
                    error.response?.status === 403 &&
                    axiosErrorSchema.safeParse(error).data?.response.data.error
                      .code === "under_18_restriction"
                  ) {
                    bookingDataLayerPush({
                      event: "customer_create_under_18_web_app",
                    });

                    errorAlert(
                      error,
                      intl.formatMessage({
                        id: "LtLbS2",
                        defaultMessage: "You must be 18 or older",
                        description:
                          "Title for error message when a user under 18 tries to register",
                      }),
                      intl.formatMessage({
                        id: "SSJQtP",
                        defaultMessage:
                          "You need to be at least 18 years old to register and book online. Please contact the clinic for assistance.",
                        description:
                          "Description for error message when a user under 18 tries to register",
                      }),
                      intl.formatMessage({
                        id: "Hp/8ql",
                        defaultMessage: "Ok close",
                        description: "Button text for error message",
                      }),
                      undefined,
                      true,
                    );

                    return;
                  }

                  // If the user already exists, continue with the booking process
                  // even though that should not happen
                  if (error.response?.status !== 409) {
                    throw error;
                  }
                  // User already exists, continue with the booking process
                  // Fetch active user in case ot got removed
                  await store.authStore.getActiveUser();
                }
              }

              if (store.bookingStore.hasNewPetValues) {
                console.log("😼 Creating new pet");
                const pet = await store.petsStore.createPet({
                  name: store.bookingStore.newPetName as string,
                  speciesId: store.bookingStore.newPetSpeciesId as string,
                  genderId: "0", // default to fix bug on BE
                });

                console.log("😼 Setting new pet");

                store.bookingStore.setPatientId(pet.id);
                store.bookingStore.setPatientSpeciesId(pet.species?.id ?? null);
              }

              if (store.bookingStore.existingClientHasNewDetails) {
                console.log("😼 Updating existing user email");

                // Update existing user (mostly used for existing users without email)
                await store.userStore.updateActiveUser({
                  email: store.bookingStore.existingClientEmail,
                  phoneNumber: store.bookingStore.existingClientPhoneNumber,
                  streetAddress: store.bookingStore.existingClientStreetAddress,
                  zipCode: store.bookingStore.existingClientZipCode,
                  city: store.bookingStore.existingClientCity,
                });
              }

              console.log("😼 Booking appointment");

              // bookAppointment will use data from the store to book the appointment
              const appointment = await store.bookingStore.bookAppointment();

              // Save appointment id so we can send it to GTM later
              store.bookingStore.setAppointmentId(appointment.id);

              console.log("😼 Appointment booked");

              console.log("😼 Dismissing router");

              // Navigate to the success screen if everything went well
              router.dismissAll();

              if (isStandAloneBookingFlow) {
                console.log("😼 Navigating to standalone success screen");
                router.replace({
                  pathname: "/(standalone)/booking/success",
                  params: {
                    clinicId: standaloneClinicId,
                    isNewUser: isNewUser ? "1" : "0",
                  },
                });
              } else {
                console.log("😼 Navigating to app success screen");
                router.replace({
                  pathname: "/(app)/book-appointment/success",
                });
              }
            } catch (error) {
              console.log(error);
              errorAlert(error);
              Sentry.captureException(error);
            }
          }}
          disabled={!tcCheckbox}
        />
      </View>
    </>
  );
});

const ContactDetails = observer(function ContactDetails() {
  const { isStandAloneBookingFlow, isNewUser, clinicId } =
    useStandaloneBookingFlow();
  const store = useStore();
  const {
    newClientFirstName,
    newClientLastName,
    newClientPhoneNumber,
    newClientEmail,
    existingClientEmail,
    existingClientPhoneNumber,
  } = store.bookingStore;

  const { activeUser } = store.authStore;

  const firstName = newClientFirstName || activeUser?.firstName;
  const lastName = newClientLastName || activeUser?.lastName;
  const phoneNumber =
    existingClientEmail || newClientPhoneNumber || activeUser?.phoneNumber;
  const email =
    existingClientPhoneNumber || newClientEmail || activeUser?.email;

  return (
    <View padding16 colorPrimaryBg>
      <View flexDirectionRow justifyContentSpaceBetween>
        <BrandText sizeH3 weightBold colorBlack>
          <FormattedMessage
            id="gclMyN"
            defaultMessage="Contact information"
            description="Title for the contact informaton section"
          />
        </BrandText>

        <IconButton
          iconName="edit"
          iconSize={24}
          iconColor={styleConstantsV2.colorBlack}
          onPress={() => {
            if (isStandAloneBookingFlow) {
              if (isNewUser) {
                router.navigate({
                  pathname: "/(standalone)/booking/add-client-details",
                  params: {
                    clinicId: clinicId,
                    isNewUser: "1",
                  },
                });
              } else {
                if (activeUser?.email) {
                  router.navigate({
                    pathname: "/(standalone)/booking/edit-contact-details",
                    params: {
                      clinicId: clinicId,
                      isNewUser: "0",
                    },
                  });
                } else {
                  router.navigate({
                    pathname:
                      "/(standalone)/booking/add-existing-client-details",
                    params: {
                      clinicId: clinicId,
                      isNewUser: "0",
                    },
                  });
                }
              }

              return;
            } else {
              router.navigate("/(app)/book-appointment/edit-contact-details");
            }
          }}
        />
      </View>
      <Spacer small />
      <BodyText sizeLarge colorBlack>
        {firstName} {lastName}
      </BodyText>
      <Spacer small />
      <BodyText sizeLarge colorBlack>
        {phoneNumber}
      </BodyText>
      <Spacer small />
      <BodyText sizeLarge colorBlack>
        {email}
      </BodyText>
    </View>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const OverviewScreenScreenWithBookingData = withBookingData(
  OverviewScreen,
  ["clinicId", "patientId", "reasonId", "practitionerId", "start", "end"],
);
